<script setup lang="ts">
const props = withDefaults(defineProps<{
  products: Array<{ title: string; price: string; category: string; images: Array<{ url: string; altText: string | null }>; slug: string; tags?: string[] }>
  eyebrow?: string
  title?: string
  ctaLabel?: string
  ctaHref?: string
}>(), {
  eyebrow: 'CURATED GOODS',
  title: 'From the Goods',
  ctaLabel: 'Visit the Goods',
  ctaHref: '/shop',
})

// Section entrance
const sectionRef = ref<HTMLElement | null>(null)
const sectionVisible = ref(false)

// Staggered card entrance
const gridRef = ref<HTMLElement | null>(null)
const visibleCards = reactive(new Set<number>())

// Scroll-linked horizontal parallax via scrollLeft
let rafId = 0
let currentScroll = 0
let targetScroll = 0
let isLerping = false

function lerp() {
  const grid = gridRef.value
  if (!grid) return

  const diff = targetScroll - currentScroll
  if (Math.abs(diff) < 0.5) {
    currentScroll = targetScroll
    grid.scrollLeft = currentScroll
    isLerping = false
    return
  }

  currentScroll += diff * 0.08
  grid.scrollLeft = currentScroll
  requestAnimationFrame(lerp)
}

function onScroll() {
  cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(() => {
    const section = sectionRef.value
    const grid = gridRef.value
    if (!section || !grid) return

    const rect = section.getBoundingClientRect()
    const vh = window.innerHeight
    const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)))
    const overflow = grid.scrollWidth - grid.clientWidth
    const cardWidth = grid.firstElementChild?.getBoundingClientRect().width ?? 0
    const maxShift = Math.min(overflow * 0.3, cardWidth * 0.8)
    targetScroll = progress * maxShift

    if (!isLerping) {
      isLerping = true
      requestAnimationFrame(lerp)
    }
  })
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()

  // Section curtain observer
  const sectionObs = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        sectionVisible.value = true
        sectionObs.disconnect()
      }
    },
    { threshold: 0, rootMargin: '0px' },
  )
  if (sectionRef.value) sectionObs.observe(sectionRef.value)

  // Grid observer — staggered card visibility
  const gridObs = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        props.products.forEach((_, i) => {
          setTimeout(() => visibleCards.add(i), i * 100)
        })
        gridObs.disconnect()
      }
    },
    { threshold: 0.15 },
  )
  if (gridRef.value) gridObs.observe(gridRef.value)

  onUnmounted(() => {
    sectionObs.disconnect()
    gridObs.disconnect()
    window.removeEventListener('scroll', onScroll)
    cancelAnimationFrame(rafId)
  })
})
</script>

<template>
  <section
    ref="sectionRef"
    :class="[
      'shop-section bg-dark w-full transition-[clip-path] duration-500 ease-out',
      sectionVisible ? 'shop-section--visible' : '',
    ]"
  >
    <div class="max-w-7xl mx-auto px-4 md:px-20 py-12 md:py-20 flex flex-col gap-4 md:gap-8">
      <AppSectionHeader :eyebrow="eyebrow" :title="title" :ctaLabel="ctaLabel" :ctaHref="ctaHref" :dark="true" />

      <!-- Horizontal scroll card row -->
      <div
        ref="gridRef"
        class="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
      >
        <div
          v-for="(product, i) in products"
          :key="product.slug"
          class="w-[75%] sm:w-[45%] lg:w-[calc(25%-12px)] flex-shrink-0"
        >
          <AppProductCard
            :title="product.title"
            :price="product.price"
            :images="product.images"
            :handle="product.slug.replace('/shop/', '')"
            :category="product.category"
            :dark="true"
            :tags="product.tags"
            :compact="true"
            :animated="true"
            :visible="visibleCards.has(i)"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Curtain-rise entrance */
.shop-section {
  clip-path: inset(100% 0 0 0);
}

.shop-section--visible {
  clip-path: inset(0% 0 0 0);
}

/* Hide scrollbar */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
